import { useReducer, useCallback, useEffect } from 'react';
// TODO HL-1430 move this util into our package once JWM business page is replaced with ours
import {
	usePagination,
	type ResponseData,
} from '@atlassian/jira-business-pagination/src/index.tsx';
import { useResource, type RouteResource } from '@atlassian/react-resource-router';
import type { TotalCountMetaData } from '../../services/index.tsx';

type State<T> = {
	data: T | null;
	loading: boolean;
	error: Error | null;
};

type Action<T> =
	| { type: 'FETCH_START' }
	| { type: 'FETCH_SUCCESS'; payload: T }
	| { type: 'FETCH_FAILURE'; payload: Error };

const reducer = <T,>(state: State<T>, action: Action<T>): State<T> => {
	switch (action.type) {
		case 'FETCH_START':
			return { ...state, loading: true, error: null };
		case 'FETCH_SUCCESS':
			return { ...state, loading: false, data: action.payload };
		case 'FETCH_FAILURE':
			return { ...state, loading: false, error: action.payload };
		default:
			return state;
	}
};

export const useSummaryData = <T,>(fetchDataFn: (after?: string | null) => Promise<T>) => {
	const initialState: State<T> = {
		data: null,
		loading: true,
		error: null,
	};

	const [{ data, loading, error }, dispatch] = useReducer(reducer<T>, initialState);

	const fetchData = useCallback(async () => {
		dispatch({ type: 'FETCH_START' });
		try {
			const response = await fetchDataFn();
			dispatch({ type: 'FETCH_SUCCESS', payload: response });
		} catch (err) {
			// eslint-disable-next-line @typescript-eslint/consistent-type-assertions
			dispatch({ type: 'FETCH_FAILURE', payload: err as Error });
		}
	}, [fetchDataFn]);

	useEffect(() => {
		fetchData();
	}, [fetchData]);

	return {
		data,
		loading,
		error,
		fetchData,
	};
};

export const usePaginatedSummaryData = <T,>(
	fetchDataFn: (after: string | null) => Promise<ResponseData<T> & TotalCountMetaData>,
	options?: { fetchMode?: 'append' | 'replace' },
) => {
	const initialFetchData = useCallback(() => fetchDataFn(null), [fetchDataFn]);

	const {
		data: initialData,
		loading: initialLoading,
		error: initialError,
		fetchData: initialFetch,
	} = useSummaryData(initialFetchData);

	return usePaginatedData({
		initialData,
		initialLoading,
		initialError,
		initialFetch,
		fetchDataFn,
		options,
	});
};

// TODO clean up or refactor as a part of HL-1930
export const useRouteResourceAndPaginatedSummaryData = <D, T>(
	routeResource: RouteResource<D>,
	dataSelector: (data: D) => ResponseData<T> & TotalCountMetaData,
	fetchDataFn: (after: string | null) => Promise<ResponseData<T> & TotalCountMetaData>,
	options?: { fetchMode?: 'append' | 'replace' },
) => {
	const {
		data: initialRouteResourceData,
		loading: initialLoading,
		error: initialError,
		refresh: initialFetch,
	} = useResource(routeResource);
	const initialData = initialRouteResourceData && dataSelector(initialRouteResourceData);

	return usePaginatedData({
		initialData,
		initialLoading,
		initialError,
		initialFetch,
		fetchDataFn,
		options,
	});
};

const usePaginatedData = <T,>({
	initialData,
	initialLoading,
	initialError,
	initialFetch,
	fetchDataFn,
	options,
}: {
	initialData: (ResponseData<T> & TotalCountMetaData) | null;
	initialLoading: boolean;
	// eslint-disable-next-line @typescript-eslint/no-explicit-any
	initialError: Error | Record<string, any> | null;
	initialFetch: () => void;
	fetchDataFn: (after: string | null) => Promise<ResponseData<T> & TotalCountMetaData>;
	options?: { fetchMode?: 'append' | 'replace' };
}) => {
	const {
		data,
		error: nextPageError,
		hasNextPage,
		loadingNext,
		fetch,
		fetchNext,
		reset,
		loading,
	} = usePagination<T, Error>(fetchDataFn, options);

	useEffect(() => {
		if (initialData != null) {
			reset(initialData);
		}
	}, [initialData, reset]);

	// TODO: HL-1514 if this is used for retries, it would reset to first page. Implement something which uses
	// `fetch` from `usePagination` but also includes args for the current page that needs to be refetched
	const retry = useCallback(() => {
		if (initialData != null) {
			reset(initialData);
		} else {
			initialFetch();
		}
	}, [initialFetch, reset, initialData]);

	// TODO HL-1370 this is to handle the endpoint used by JWM for IssuesByTypeData and IssuesByAssigneeData
	// which requires this special handling as only the initial call includes this information, but when we
	// replace with our API we need to update this logic for how our endpoint returns metadata
	const metaData = { totalUnresolvedIssues: initialData?.totalUnresolvedIssues };

	return {
		data,
		loading: initialLoading || loading,
		loadingNext,
		error: initialError || nextPageError,
		hasNextPage,
		retry,
		fetch,
		fetchNext,
		metaData,
	};
};
